import { resolveChoiceValueLabel, resolveChoiceValueListLabels } from "../model/incidentLabeling";
import type { IncidentDetailCardRecord } from "../model/incidentTypes";

type IncidentDetailCardProps = {
  card: IncidentDetailCardRecord;
};

type DetailRow = {
  label: string;
  value: string | number | null | undefined;
};

function hasDisplayValue(value: string | number | null | undefined): boolean {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return false;
}

function formatChoiceValueList(
  values: Array<{ code: string; label: string | null }> | null | undefined,
  group:
    | "incidentType"
    | "locationType"
    | "perpetrator"
    | "relationship"
    | "gender"
    | "aliveStatus"
    | "burialSiteType"
    | "estimatedBuried",
): string | null {
  if (!values || values.length === 0) {
    return null;
  }

  return resolveChoiceValueListLabels(values, group).join(", ");
}

function formatChoiceValue(
  value: { code: string; label: string | null } | null | undefined,
  group:
    | "incidentType"
    | "locationType"
    | "perpetrator"
    | "relationship"
    | "gender"
    | "aliveStatus"
    | "burialSiteType"
    | "estimatedBuried",
): string | null {
  if (!value) {
    return null;
  }

  return resolveChoiceValueLabel(value, group);
}

function buildCardTitle(card: IncidentDetailCardRecord): string {
  const indexLabel = `#${card.index + 1}`;

  switch (card.family) {
    case "tortureOrRape":
      return `Torture/Rape Incident ${indexLabel}`;
    case "murdered":
      return `Murdered Person Report ${indexLabel}`;
    case "missingOrAbducted":
      return `Missing/Abducted Report ${indexLabel}`;
    case "massGrave":
      return `Mass Grave Report ${indexLabel}`;
    default:
      return `Incident Record ${indexLabel}`;
  }
}

function buildRows(card: IncidentDetailCardRecord): DetailRow[] {
  switch (card.family) {
    case "tortureOrRape":
      return [
        {
          label: "Incident Types",
          value: formatChoiceValueList(card.incident.incidentTypes, "incidentType"),
        },
        {
          label: "Date",
          value: card.incident.date ?? null,
        },
        {
          label: "Location Type",
          value: formatChoiceValue(card.incident.locationType, "locationType"),
        },
        {
          label: "Other Location",
          value: card.incident.locationOther ?? null,
        },
        {
          label: "Township",
          value: card.incident.township ?? null,
        },
        {
          label: "Village",
          value: card.incident.villageName ?? null,
        },
        {
          label: "Perpetrators",
          value: formatChoiceValueList(card.incident.perpetrators, "perpetrator"),
        },
      ];

    case "murdered":
      return [
        {
          label: "Relationship",
          value: formatChoiceValue(card.incident.relationship, "relationship"),
        },
        {
          label: "Gender",
          value: formatChoiceValue(card.incident.gender, "gender"),
        },
        {
          label: "Age",
          value: card.incident.age ?? null,
        },
        {
          label: "Date",
          value: card.incident.date ?? null,
        },
        {
          label: "Location Type",
          value: formatChoiceValue(card.incident.locationType, "locationType"),
        },
        {
          label: "Other Location",
          value: card.incident.locationOther ?? null,
        },
        {
          label: "Township",
          value: card.incident.township ?? null,
        },
        {
          label: "Village",
          value: card.incident.villageName ?? null,
        },
        {
          label: "Perpetrators",
          value: formatChoiceValueList(card.incident.perpetrators, "perpetrator"),
        },
      ];

    case "missingOrAbducted":
      return [
        {
          label: "Relationship",
          value: formatChoiceValue(card.incident.relationship, "relationship"),
        },
        {
          label: "Gender",
          value: formatChoiceValue(card.incident.gender, "gender"),
        },
        {
          label: "Age",
          value: card.incident.age ?? null,
        },
        {
          label: "Date",
          value: card.incident.date ?? null,
        },
        {
          label: "Location Type",
          value: formatChoiceValue(card.incident.locationType, "locationType"),
        },
        {
          label: "Other Location",
          value: card.incident.locationOther ?? null,
        },
        {
          label: "Township",
          value: card.incident.township ?? null,
        },
        {
          label: "Village",
          value: card.incident.villageName ?? null,
        },
        {
          label: "Is Alive",
          value: formatChoiceValue(card.incident.isAlive, "aliveStatus"),
        },
        {
          label: "Perpetrators",
          value: formatChoiceValueList(card.incident.perpetrators, "perpetrator"),
        },
      ];

    case "massGrave":
      return [
        {
          label: "Township",
          value: card.incident.township ?? null,
        },
        {
          label: "Village",
          value: card.incident.villageName ?? null,
        },
        {
          label: "Burial Site Type",
          value: formatChoiceValueList(card.incident.burialSiteType, "burialSiteType"),
        },
        {
          label: "Estimated Buried",
          value: formatChoiceValue(card.incident.estimatedBuried, "estimatedBuried"),
        },
      ];

    default:
      return [];
  }
}

export function IncidentDetailCard({ card }: IncidentDetailCardProps) {
  const title = buildCardTitle(card);
  const rows = buildRows(card).filter((row) => hasDisplayValue(row.value));

  return (
    <section className="incident-detail-card">
      <p className="small-meta strong incident-detail-card__title">{title}</p>
      {rows.length > 0 ? (
        <dl className="detail-pairs detail-pairs-stack incident-detail-card__rows">
          {rows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}:</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="small-paragraph">No detailed fields were reported for this entry.</p>
      )}
    </section>
  );
}
